/**
 * TreeFileBrowser Component (Option D)
 *
 * Windows Explorer-style split-panel file browser with:
 * - Left panel: Tree showing folder hierarchy from all sites
 * - Right panel: DataGrid showing contents of selected folder
 *
 * Uses React Query for caching with staleTime derived from backend cache settings.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  makeStyles,
  tokens,
  Tree,
  TreeItem,
  TreeItemLayout,
  TreeOpenChangeData,
  TreeOpenChangeEvent,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  TableColumnSizingOptions,
  TableColumnId,
  createTableColumn,
  Spinner,
  Text,
  mergeClasses,
  Toolbar,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Input,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Label,
} from '@fluentui/react-components';
import {
  Folder24Filled,
  Folder20Filled,
  ArrowClockwise24Regular,
  Home20Regular,
  ArrowUpload24Regular,
  Document24Regular,
  FolderOpen24Regular,
  Search20Regular,
  Add24Regular,
  Delete24Regular,
  MoreHorizontal20Regular,
  Open20Regular,
  ArrowDownload20Regular,
  ArrowDownload24Regular,
  Rename20Regular,
  Copy20Regular,
  Cut20Regular,
  ClipboardPaste24Regular,
  FolderArrowRight20Regular,
} from '@fluentui/react-icons';
import { SharePointIcon } from '../icons/SharePointIcon';
import { AsyncToolbarButton } from '../common/AsyncToolbarButton';
import { TeamsIcon } from '../icons/TeamsIcon';
import { fileIcons, defaultFileIcon } from '../../assets/file-icons';
import { useFileBrowserSites } from '../../hooks/useFileBrowserSites';
import { useUserTeams } from '../../hooks/useUserTeams';
import {
  useFileBrowserFolderContents,
  folderContentsKeys,
} from '../../hooks/useFileBrowserFolderContents';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useDelegatedToken } from '../../hooks/useDelegatedToken';
import { TenantApiService } from '../../services/tenantApi';
import { getFileOperationError } from '../../utils/fileOperationErrors';
import type { FolderItem, SearchResultItem } from '../../types/sharepoint';

interface TreeFileBrowserProps {
  tenantId: string;
}

/**
 * Selected folder info for grid display
 */
interface SelectedFolder {
  siteId: string;
  folderId: string;
  name: string;
}

/**
 * Breadcrumb path item
 */
interface BreadcrumbPathItem {
  id: string;
  name: string;
  siteId: string;
  folderId: string;
}

/**
 * Node info for tracking which site a folder belongs to.
 */
interface NodeInfo {
  siteId: string;
}

/**
 * Clipboard item for copy/cut operations.
 */
interface ClipboardItem {
  item: FolderItem;
  siteId: string;
  sourceFolderId: string;
  operation: 'copy' | 'cut';
}

// localStorage keys for persisting dimensions
const NAV_PANE_WIDTH_KEY = 'treeBrowser.navPaneWidth';
const CONTAINER_HEIGHT_KEY = 'treeBrowser.containerHeight';
const COLUMN_SIZING_KEY = 'treeBrowser.columnSizing';
const DEFAULT_NAV_PANE_WIDTH = 280;
const DEFAULT_CONTAINER_HEIGHT = 600;
const MIN_NAV_PANE_WIDTH = 150;
const MIN_CONTAINER_HEIGHT = 300;
const MAX_CONTAINER_HEIGHT = 1200;
const MAX_NAV_PANE_WIDTH_PERCENT = 0.5; // 50% of container

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '600px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '12px', // Match other workspace cards
    overflow: 'hidden',
    position: 'relative',
  },
  splitPanels: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  treePanel: {
    minWidth: '150px',
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: 'auto',
    padding: tokens.spacingVerticalS,
    flexShrink: 0,
  },
  resizeHandle: {
    width: '4px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    flexShrink: 0,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground,
    },
  },
  resizeHandleActive: {
    backgroundColor: tokens.colorBrandBackground,
  },
  heightResizeHandle: {
    height: '4px',
    cursor: 'row-resize',
    backgroundColor: 'transparent',
    flexShrink: 0,
    transition: 'background-color 0.15s ease',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground,
    },
  },
  heightResizeHandleActive: {
    backgroundColor: tokens.colorBrandBackground,
  },
  contentPanel: {
    flex: 1,
    overflow: 'hidden',
    minWidth: '300px',
    display: 'flex',
    flexDirection: 'column',
  },
  toolbar: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    flexShrink: 0,
  },
  breadcrumbContainer: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  gridPanel: {
    flex: 1,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
  },
  gridContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative',
  },
  gridContentDropActive: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `2px dashed ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  // Drop target highlight for internal drag-and-drop (folder rows and tree items)
  dropTargetHighlight: {
    backgroundColor: tokens.colorBrandBackground2,
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: '-2px',
  },
  dataGrid: {
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    zIndex: 10,
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground4,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground4,
  },
  // File/folder item styling
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    overflow: 'hidden',
    minWidth: 0, // Allow flex item to shrink below content size
    width: '100%',
  },
  fileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    flex: 1,
  },
  folderLink: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    cursor: 'pointer',
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: tokens.colorBrandForeground1,
    },
  },
  // Icon colors
  folderIcon: {
    color: '#dcb67a', // Classic yellow/gold folder color
  },
  // Microsoft file type icon
  fileTypeIcon: {
    width: '24px',
    height: '24px',
    flexShrink: 0,
  },
  // Grid styling
  gridHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  gridHeaderCell: {
    fontWeight: tokens.fontWeightSemibold,
  },
  dateColumn: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  sizeColumn: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'right',
  },
  // Row styling - light blue hover for all rows, show action button on hover
  gridRow: {
    '&:hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
    '&:active': {
      backgroundColor: tokens.colorBrandBackground2,
    },
    // Show action button on row hover
    '&:hover .action-menu-button': {
      opacity: 1,
    },
  },
  // Action menu button - hidden by default, shown on row hover
  actionMenuButton: {
    opacity: 0,
    transition: 'opacity 0.15s ease-in-out',
    marginLeft: 'auto',
    flexShrink: 0,
  },
  // Selection styling for grid rows
  selectedRow: {
    backgroundColor: tokens.colorBrandBackground2,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
    '&:active': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  // Selection styling for tree item layouts
  selectedTreeItemLayout: {
    backgroundColor: tokens.colorBrandBackground2,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  // Upload toast notification
  uploadToast: {
    position: 'absolute',
    bottom: tokens.spacingVerticalL,
    right: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    boxShadow: tokens.shadow16,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    zIndex: 1000,
  },
  // Search input in toolbar
  searchInput: {
    minWidth: '200px',
    marginLeft: 'auto',
  },
  // Search results indicator in breadcrumb
  searchResultsIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

/**
 * Get file icon based on file extension using locally stored Microsoft icons.
 * Icons are stored in src/assets/file-icons/ for instant loading.
 */
function getFileIcon(fileName: string, styles: ReturnType<typeof useStyles>): JSX.Element {
  const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
  const iconSrc = fileIcons[extension] ?? defaultFileIcon;

  return (
    <img
      src={iconSrc}
      alt={`${extension} file`}
      className={styles.fileTypeIcon}
    />
  );
}

/**
 * Format date and time for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format file size in human-readable format.
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(exponent > 0 ? 1 : 0)} ${units[exponent]}`;
}

/**
 * TreeFileBrowser - Split panel file browser with tree navigation.
 *
 * Uses React Query for caching folder contents, with staleTime derived
 * from backend cache settings for optimal performance.
 */
export function TreeFileBrowser({ tenantId }: TreeFileBrowserProps): JSX.Element {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { getDelegatedToken } = useDelegatedToken();
  const containerRef = useRef<HTMLDivElement>(null);

  // Hidden file input refs for upload functionality
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Nav pane width state with localStorage persistence
  const [navPaneWidth, setNavPaneWidth] = useState<number>(() => {
    const saved = localStorage.getItem(NAV_PANE_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_NAV_PANE_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);

  // Container height state with localStorage persistence
  const [containerHeight, setContainerHeight] = useState<number>(() => {
    const saved = localStorage.getItem(CONTAINER_HEIGHT_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_CONTAINER_HEIGHT;
  });
  const [isResizingHeight, setIsResizingHeight] = useState(false);

  // Persist nav pane width to localStorage
  useEffect(() => {
    localStorage.setItem(NAV_PANE_WIDTH_KEY, navPaneWidth.toString());
  }, [navPaneWidth]);

  // Persist container height to localStorage
  useEffect(() => {
    localStorage.setItem(CONTAINER_HEIGHT_KEY, containerHeight.toString());
  }, [containerHeight]);

  // Handle resize drag
  const handleResizeMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setIsResizing(true);
  }, []);

  // Mouse move and mouse up handlers for horizontal resize (nav pane width)
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const maxWidth = containerRect.width * MAX_NAV_PANE_WIDTH_PERCENT;
      const newWidth = Math.max(
        MIN_NAV_PANE_WIDTH,
        Math.min(maxWidth, event.clientX - containerRect.left)
      );
      setNavPaneWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Handle vertical resize drag (container height)
  const handleHeightResizeMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setIsResizingHeight(true);
  }, []);

  // Mouse move and mouse up handlers for vertical resize (container height)
  useEffect(() => {
    if (!isResizingHeight) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newHeight = Math.max(
        MIN_CONTAINER_HEIGHT,
        Math.min(MAX_CONTAINER_HEIGHT, event.clientY - containerRect.top)
      );
      setContainerHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizingHeight(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingHeight]);

  // Fetch sites using React Query
  const {
    sites,
    isLoading: sitesLoading,
    isFetching: sitesFetching,
    refetch: refetchSites,
  } = useFileBrowserSites({ tenantId });

  // Fetch teams with channels to identify channel folders
  const { teams } = useUserTeams({ tenantId });

  // Build a lookup of channel names per team/site
  // Key: site displayName (which matches team displayName)
  // Value: Set of channel displayNames
  const channelsByTeamName = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const team of teams) {
      const channelNames = new Set((team.channels ?? []).map((c) => c.displayName));
      map.set(team.displayName, channelNames);
    }
    return map;
  }, [teams]);

  /**
   * Check if a folder is a Teams channel folder.
   * A folder is a channel folder if:
   * 1. Its parent site has a displayName matching a Team displayName
   * 2. The folder name matches a channel displayName in that team
   */
  const isChannelFolder = useCallback((folderName: string, siteId: string): boolean => {
    // Find the site to get its displayName
    const site = sites.find((s) => s.id === siteId);
    if (!site) return false;

    // Check if this site corresponds to a team
    const channels = channelsByTeamName.get(site.displayName);
    if (!channels) return false;

    // Check if the folder name matches a channel name
    return channels.has(folderName);
  }, [sites, channelsByTeamName]);

  // Track expanded nodes in tree (controlled by chevron clicks)
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  // Track loaded folder children for tree rendering
  // (Also cached in React Query, but we need local state for rendering)
  const [loadedChildren, setLoadedChildren] = useState<Map<string, FolderItem[]>>(new Map());

  // Track which site each folder belongs to (for API calls)
  const [nodeInfoMap, setNodeInfoMap] = useState<Map<string, NodeInfo>>(new Map());

  // Currently selected folder for grid display
  const [selectedFolder, setSelectedFolder] = useState<SelectedFolder | null>(null);

  // Breadcrumb path for navigation
  const [breadcrumbPath, setBreadcrumbPath] = useState<BreadcrumbPathItem[]>([]);

  // Multi-select state for grid items
  const [selectedGridItems, setSelectedGridItems] = useState<Set<string>>(new Set());
  const [selectionAnchor, setSelectionAnchor] = useState<string | null>(null);

  // Loading state for content fetching (shows spinner in grid)
  const [isLoadingContents, setIsLoadingContents] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultItem[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // New folder dialog state
  const [isNewFolderDialogOpen, setIsNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Clipboard state for copy/cut operations
  const [clipboard, setClipboard] = useState<ClipboardItem | null>(null);
  const [isPasting, setIsPasting] = useState(false);

  // Download state
  const [isDownloading, setIsDownloading] = useState(false);

  // Paste toast notification state
  const [pasteToast, setPasteToast] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Auto-dismiss paste toast after 3 seconds
  useEffect(() => {
    if (pasteToast) {
      const timer = setTimeout(() => setPasteToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [pasteToast]);

  // Auto-expand tree ancestors when breadcrumb path changes (syncs tree with grid navigation)
  // Only run when navigating into folders (length > 1), since site expansion is handled elsewhere
  useEffect(() => {
    if (breadcrumbPath.length > 1) {
      // Expand all items in the breadcrumb path so the tree shows the current location
      const pathIds = breadcrumbPath.map((item) => item.id);
      setOpenItems((previous) => {
        // Check if any new items need to be added
        const hasNewItems = pathIds.some((id) => !previous.has(id));
        if (!hasNewItems) {
          return previous; // Return same reference to avoid unnecessary re-render
        }
        const updated = new Set(previous);
        for (const id of pathIds) {
          updated.add(id);
        }
        return updated;
      });
    }
  }, [breadcrumbPath]);

  // Ref for auto-scrolling the selected tree item into view
  const selectedTreeItemRef = useRef<HTMLDivElement>(null);

  // Auto-scroll tree to show selected item when selection changes
  useEffect(() => {
    if (selectedTreeItemRef.current && typeof selectedTreeItemRef.current.scrollIntoView === 'function') {
      selectedTreeItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedFolder?.folderId]);

  // Rename dialog state
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState<FolderItem | null>(null);
  const [newItemName, setNewItemName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Error dialog state (for prominent errors like file-in-use)
  const [errorDialog, setErrorDialog] = useState<{
    title: string;
    message: string;
  } | null>(null);

  // External file drop state
  const [isDragOver, setIsDragOver] = useState(false);

  // Internal drag-to-folder state - tracks both folder ID and location (tree vs grid)
  const [dropTarget, setDropTarget] = useState<{ folderId: string; location: 'tree' | 'grid' } | null>(null);
  const dropTargetRef = useRef<{ folderId: string; location: 'tree' | 'grid' } | null>(null);

  // Wrapper to update both state and ref, but only if value changes
  const updateDropTarget = useCallback((newValue: { folderId: string; location: 'tree' | 'grid' } | null) => {
    const current = dropTargetRef.current;
    const isSame = current === newValue ||
      (current && newValue && current.folderId === newValue.folderId && current.location === newValue.location);
    if (!isSame) {
      dropTargetRef.current = newValue;
      setDropTarget(newValue);
    }
  }, []);

  // Column sizing options - loaded once from localStorage on mount, never updated during session
  // This ensures the hook gets stable options and doesn't reset column widths
  const [columnSizingOptions] = useState<TableColumnSizingOptions>(() => {
    const saved = localStorage.getItem(COLUMN_SIZING_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall back to defaults if parsing fails
      }
    }
    return {
      name: { minWidth: 150, defaultWidth: 400 },
      modified: { minWidth: 100, defaultWidth: 140 },
      modifiedBy: { minWidth: 100, defaultWidth: 150 },
      size: { minWidth: 60, defaultWidth: 80 },
    };
  });

  // Fetch selected folder contents using React Query
  const {
    items: gridContents,
    isLoading: isLoadingGrid,
    isFetching: isFetchingGrid,
    refetch: refetchFolderContents,
  } = useFileBrowserFolderContents({
    tenantId,
    siteId: selectedFolder?.siteId ?? '',
    folderId: selectedFolder?.folderId ?? '',
    enabled: !!selectedFolder,
  });

  // Convert sites to FolderItem format for display in grid when at root level
  const sitesAsGridItems: FolderItem[] = useMemo(() => {
    return sites.map((site) => ({
      id: site.id,
      name: site.displayName,
      webUrl: site.webUrl,
      size: 0,
      lastModifiedDateTime: '',
      lastModifiedBy: '',
      isFolder: true, // Sites behave like folders for navigation
      parentId: 'sites-root',
    }));
  }, [sites]);

  // Determine what to show in the grid: sites (at root) or folder contents
  const effectiveGridContents = selectedFolder ? gridContents : sitesAsGridItems;
  const isAtSitesRoot = !selectedFolder;

  // File upload hook for handling file/folder uploads
  const { uploadFiles, uploadFolder, isUploading, error: uploadError } = useFileUpload({
    tenantId,
    siteId: selectedFolder?.siteId ?? '',
    folderId: selectedFolder?.folderId ?? '',
    onUploadComplete: () => {
      // Refetch folder contents with refresh=true to bypass backend cache
      refetchFolderContents();
    },
  });

  // Handle tree node expansion (chevron click only)
  // This only manages expand/collapse state and loads children for the tree
  const handleOpenChange = useCallback(
    async (_event: TreeOpenChangeEvent, data: TreeOpenChangeData) => {
      const nodeValue = data.value as string;
      const isOpening = data.open;

      if (isOpening) {
        // Add to open items immediately for visual feedback
        setOpenItems((prev) => new Set(prev).add(nodeValue));

        // Check if we already have children loaded for tree
        if (loadedChildren.has(nodeValue)) {
          return; // Already loaded
        }

        // Determine if this is a site or a folder
        const site = sites.find((s) => s.id === nodeValue);
        const nodeInfo = nodeInfoMap.get(nodeValue);

        let siteId: string;
        let folderId: string;

        if (site) {
          siteId = site.id;
          folderId = 'root';
        } else if (nodeInfo) {
          siteId = nodeInfo.siteId;
          folderId = nodeValue;
        } else {
          return;
        }

        try {
          // Fetch folder contents for tree children
          const queryKey = folderContentsKeys.folder(tenantId, siteId, folderId);
          const children = await queryClient.fetchQuery({
            queryKey,
            queryFn: () => TenantApiService.getFolderContents(tenantId, siteId, folderId, false),
          });

          // Filter to only folders for tree navigation
          const folders = children.filter((item) => item.isFolder);

          // Store folders for tree rendering
          setLoadedChildren((prev) => new Map(prev).set(nodeValue, folders));

          // Update node info map for child folders
          setNodeInfoMap((prev) => {
            const newMap = new Map(prev);
            for (const folder of folders) {
              newMap.set(folder.id, { siteId });
            }
            return newMap;
          });
        } catch {
          // Error handled by React Query
        }
      } else {
        // Collapse: remove from open items
        setOpenItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(nodeValue);
          return newSet;
        });
      }
    },
    [sites, tenantId, nodeInfoMap, loadedChildren, queryClient]
  );

  // Handle clicking on a tree item (selects it and shows contents in grid)
  const handleItemClick = useCallback(
    async (nodeId: string, nodeName: string, siteId: string) => {
      const isSite = sites.find((s) => s.id === nodeId) !== undefined;
      const folderId = isSite ? 'root' : nodeId;

      // Update selected folder for grid display
      setSelectedFolder({ siteId, folderId, name: nodeName });

      // Update breadcrumb path
      if (isSite) {
        // Starting fresh from a site - just the site in path
        setBreadcrumbPath([{
          id: nodeId,
          name: nodeName,
          siteId: siteId,
          folderId: 'root',
        }]);
      } else {
        // Check if we're in a different site than the current breadcrumb
        const currentSiteId = breadcrumbPath.length > 0 ? breadcrumbPath[0].siteId : null;
        const isDifferentSite = currentSiteId !== siteId;

        if (isDifferentSite || breadcrumbPath.length === 0) {
          // Switched to a different site - reset breadcrumb with site + this folder
          const site = sites.find((s) => s.id === siteId);
          const siteName = site?.displayName ?? 'Site';
          setBreadcrumbPath([
            {
              id: siteId,
              name: siteName,
              siteId: siteId,
              folderId: 'root',
            },
            {
              id: nodeId,
              name: nodeName,
              siteId: siteId,
              folderId: nodeId,
            },
          ]);
        } else {
          // Same site - check if folder already exists in breadcrumb path
          const existingIndex = breadcrumbPath.findIndex((item) => item.id === nodeId);
          if (existingIndex >= 0) {
            // Navigating to an existing breadcrumb item - truncate path
            setBreadcrumbPath(breadcrumbPath.slice(0, existingIndex + 1));
          } else {
            // Check if clicked folder is a child of the last breadcrumb item
            const lastItem = breadcrumbPath[breadcrumbPath.length - 1];
            const lastItemChildren = loadedChildren.get(lastItem.id) ?? [];
            const isDirectChild = lastItemChildren.some((child) => child.id === nodeId);

            if (isDirectChild) {
              // Direct child - append to path
              setBreadcrumbPath((prev) => [
                ...prev,
                {
                  id: nodeId,
                  name: nodeName,
                  siteId: siteId,
                  folderId: nodeId,
                },
              ]);
            } else {
              // Not a direct child - user clicked elsewhere in tree, reset to site + folder
              const site = sites.find((s) => s.id === siteId);
              const siteName = site?.displayName ?? 'Site';
              setBreadcrumbPath([
                {
                  id: siteId,
                  name: siteName,
                  siteId: siteId,
                  folderId: 'root',
                },
                {
                  id: nodeId,
                  name: nodeName,
                  siteId: siteId,
                  folderId: nodeId,
                },
              ]);
            }
          }
        }
      }

      // If this folder hasn't been loaded yet, load it now
      if (!loadedChildren.has(nodeId)) {
        setIsLoadingContents(true);
        try {
          const queryKey = folderContentsKeys.folder(tenantId, siteId, folderId);
          const children = await queryClient.fetchQuery({
            queryKey,
            queryFn: () => TenantApiService.getFolderContents(tenantId, siteId, folderId, false),
          });

          const folders = children.filter((item) => item.isFolder);
          setLoadedChildren((prev) => new Map(prev).set(nodeId, folders));

          setNodeInfoMap((prev) => {
            const newMap = new Map(prev);
            for (const folder of folders) {
              newMap.set(folder.id, { siteId });
            }
            return newMap;
          });
        } catch {
          // Error handled by React Query
        } finally {
          setIsLoadingContents(false);
        }
      }
    },
    [sites, tenantId, loadedChildren, queryClient, breadcrumbPath]
  );

  // Clear selection when navigating to a new folder
  useEffect(() => {
    setSelectedGridItems(new Set());
    setSelectionAnchor(null);
  }, [selectedFolder?.folderId]);

  // Handle clicking on a grid row for selection (supports Ctrl+click and Shift+click)
  const handleGridRowClick = useCallback(
    (event: React.MouseEvent, item: FolderItem) => {
      event.stopPropagation();

      if (event.shiftKey && selectionAnchor) {
        // Shift+click: Select range from anchor to clicked item
        const anchorIndex = gridContents.findIndex((i) => i.id === selectionAnchor);
        const clickedIndex = gridContents.findIndex((i) => i.id === item.id);

        if (anchorIndex !== -1 && clickedIndex !== -1) {
          const start = Math.min(anchorIndex, clickedIndex);
          const end = Math.max(anchorIndex, clickedIndex);
          const rangeIds = gridContents.slice(start, end + 1).map((i) => i.id);

          setSelectedGridItems(new Set(rangeIds));
        }
      } else if (event.ctrlKey || event.metaKey) {
        // Ctrl/Cmd+click: Toggle selection without affecting others
        setSelectedGridItems((prev) => {
          const newSelection = new Set(prev);
          if (newSelection.has(item.id)) {
            newSelection.delete(item.id);
          } else {
            newSelection.add(item.id);
          }
          return newSelection;
        });
        // Update anchor to the clicked item
        setSelectionAnchor(item.id);
      } else {
        // Single click: Select only this item
        setSelectedGridItems(new Set([item.id]));
        setSelectionAnchor(item.id);
      }
    },
    [gridContents, selectionAnchor]
  );

  // Handle clicking on empty space in the grid to clear selection
  const handleGridEmptySpaceClick = useCallback(
    (event: React.MouseEvent) => {
      // Check if click was on a row (has role="row" in ancestors)
      const target = event.target as HTMLElement;
      const isRowClick = target.closest('[role="row"]') !== null;

      // Clear selection only if not clicking on a row
      if (!isRowClick) {
        setSelectedGridItems(new Set());
        setSelectionAnchor(null);
      }
    },
    []
  );

  // Handle internal drag start from grid rows
  const handleRowDragStart = useCallback(
    (event: React.DragEvent, item: FolderItem) => {
      if (!selectedFolder) return;

      // Determine which items to drag: if the dragged item is in the selection, drag all selected items
      // Otherwise, drag just the single item
      const itemsToMove: Array<{ id: string; name: string; isFolder: boolean }> = [];

      if (selectedGridItems.has(item.id) && selectedGridItems.size > 1) {
        // Dragging a selected item with multiple selections - include all selected items
        const selectedItemsList = gridContents?.filter((gridItem) => selectedGridItems.has(gridItem.id)) ?? [];
        for (const selectedItem of selectedItemsList) {
          itemsToMove.push({
            id: selectedItem.id,
            name: selectedItem.name,
            isFolder: selectedItem.isFolder,
          });
        }
      } else {
        // Single item drag
        itemsToMove.push({
          id: item.id,
          name: item.name,
          isFolder: item.isFolder,
        });
      }

      // Set drag data with items info for internal move operations
      const dragData = {
        items: itemsToMove,
        siteId: selectedFolder.siteId,
        sourceFolderId: selectedFolder.folderId,
      };
      event.dataTransfer.setData('application/json', JSON.stringify(dragData));
      event.dataTransfer.effectAllowed = 'move';
      // Let browser use default drag preview (shows the whole row)
    },
    [selectedFolder, selectedGridItems, gridContents]
  );

  // Handle drag over a tree folder (for internal move)
  const handleTreeFolderDragOver = useCallback(
    (event: React.DragEvent, folderId: string, location: 'tree' | 'grid') => {
      event.preventDefault();
      event.stopPropagation();
      updateDropTarget({ folderId, location });
    },
    [updateDropTarget]
  );

  // Handle drag over a non-folder row (just prevent default, no highlight)
  const handleRowDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    updateDropTarget(null);
  }, [updateDropTarget]);

  // Handle drag leave from a tree folder
  // Note: We don't clear the highlight here because dragLeave fires BEFORE
  // dragEnter on the new element, causing visible flashing. Instead, we let
  // dragOver on the new folder update the highlight, and only clear on drop
  // or when the drag operation ends (via handleRowDragEnd).
  const handleTreeFolderDragLeave = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      // Intentionally not clearing dropTarget here to prevent flashing
    },
    []
  );

  // Handle drag end (when the drag operation is cancelled or completed)
  const handleRowDragEnd = useCallback(() => {
    updateDropTarget(null);
  }, [updateDropTarget]);

  // Handle drop on a tree folder (execute move)
  const handleTreeFolderDrop = useCallback(
    async (event: React.DragEvent, targetFolderId: string, targetSiteId: string) => {
      event.preventDefault();
      event.stopPropagation();
      updateDropTarget(null);

      // Get the drag data
      const jsonData = event.dataTransfer.getData('application/json');
      if (!jsonData) {
        return;
      }

      try {
        const dragData = JSON.parse(jsonData);
        const { items, siteId: sourceSiteId, sourceFolderId } = dragData;

        // Validate: can't drop on the same folder
        if (targetFolderId === sourceFolderId) {
          return;
        }

        // Validate: must be same site (cross-site moves not supported yet)
        if (targetSiteId !== sourceSiteId) {
          return;
        }

        // Validate: must have items to move
        if (!items || items.length === 0) {
          return;
        }

        const itemIds = items.map((item: { id: string }) => item.id);

        // Store previous data for potential rollback
        const sourceQueryKey = folderContentsKeys.folder(tenantId, sourceSiteId, sourceFolderId);
        const previousSourceData = queryClient.getQueryData<FolderItem[]>(sourceQueryKey);
        const movedItems = previousSourceData?.filter((item) => itemIds.includes(item.id)) ?? [];

        const destQueryKey = folderContentsKeys.folder(tenantId, targetSiteId, targetFolderId);
        const previousDestData = queryClient.getQueryData<FolderItem[]>(destQueryKey);

        // Optimistically remove items from source folder cache
        if (previousSourceData) {
          queryClient.setQueryData<FolderItem[]>(
            sourceQueryKey,
            previousSourceData.filter((item) => !itemIds.includes(item.id))
          );
        }

        // Optimistically add items to destination folder cache (if cached)
        if (previousDestData && movedItems.length > 0) {
          // Create updated items with new parent reference
          const updatedItems = movedItems.map((movedItem) => ({
            ...movedItem,
            parentId: targetFolderId,
          }));
          queryClient.setQueryData<FolderItem[]>(
            destQueryKey,
            [...previousDestData, ...updatedItems]
          );
        }

        try {
          // Call the move API for each item using delegated token if available
          const delegatedToken = await getDelegatedToken();
          await Promise.all(
            itemIds.map((itemId: string) =>
              delegatedToken
                ? TenantApiService.moveItemDelegated(tenantId, sourceSiteId, itemId, targetFolderId, delegatedToken)
                : TenantApiService.moveItem(tenantId, sourceSiteId, itemId, targetFolderId)
            )
          );

          // Clear selection after successful move
          setSelectedGridItems(new Set());

          // Wait for Graph API to propagate the change before refetching
          await new Promise((resolve) => setTimeout(resolve, 2000));

          // Refresh both source and destination folders in parallel
          await Promise.all([
            // Refresh source folder (current folder)
            refetchFolderContents(),
            // Refresh destination folder cache (use delegated if available)
            (delegatedToken
              ? TenantApiService.getFolderContentsDelegated(tenantId, targetSiteId, targetFolderId, delegatedToken)
              : TenantApiService.getFolderContents(tenantId, targetSiteId, targetFolderId, true)
            ).then(freshDestData => queryClient.setQueryData(destQueryKey, freshDestData))
          ]);
        } catch (error) {
          // Rollback optimistic updates on error
          if (previousSourceData) {
            queryClient.setQueryData<FolderItem[]>(sourceQueryKey, previousSourceData);
          }
          if (previousDestData) {
            queryClient.setQueryData<FolderItem[]>(destQueryKey, previousDestData);
          }

          // Show user-friendly error message (dialog for locked files, toast for others)
          const operationError = getFileOperationError(error, 'move');
          if (operationError.isFileLocked) {
            setErrorDialog({
              title: 'File In Use',
              message: operationError.message,
            });
          } else {
            setPasteToast({ type: 'error', message: operationError.message });
          }
        }
      } catch {
        // Error in parsing drag data - handled silently
      }
    },
    [tenantId, queryClient, updateDropTarget, refetchFolderContents, getDelegatedToken]
  );

  // Handle external file drag over the grid content area
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }, []);

  // Handle drag leave from the grid content area
  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }, []);

  // Handle external file drop on the grid content area
  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      // Only process if a folder is selected
      if (!selectedFolder) return;

      // Extract files from dataTransfer
      const files: File[] = [];
      if (event.dataTransfer.files) {
        for (let i = 0; i < event.dataTransfer.files.length; i++) {
          files.push(event.dataTransfer.files[i]);
        }
      }

      // Upload files if any were dropped
      if (files.length > 0) {
        await uploadFiles(files);
      }
    },
    [selectedFolder, uploadFiles]
  );

  // Handle clicking on a folder in the grid to drill down
  const handleGridFolderClick = useCallback(
    (folder: FolderItem) => {
      if (!selectedFolder) return;

      // Drill down into the folder
      handleItemClick(folder.id, folder.name, selectedFolder.siteId);

      // Also expand the folder in the tree if not already expanded
      if (!openItems.has(folder.id)) {
        setOpenItems((prev) => new Set(prev).add(folder.id));
      }
    },
    [selectedFolder, handleItemClick, openItems]
  );

  // Handle clicking on a site in the grid (when at sites root level)
  const handleGridSiteClick = useCallback(
    (site: FolderItem) => {
      // For sites, the siteId is the same as the item id
      handleItemClick(site.id, site.name, site.id);

      // Also expand the site in the tree if not already expanded
      if (!openItems.has(site.id)) {
        setOpenItems((prev) => new Set(prev).add(site.id));
      }
    },
    [handleItemClick, openItems]
  );

  // Unified handler for grid item clicks (sites or folders)
  const handleGridItemClick = useCallback(
    (item: FolderItem) => {
      if (isAtSitesRoot) {
        handleGridSiteClick(item);
      } else {
        handleGridFolderClick(item);
      }
    },
    [isAtSitesRoot, handleGridSiteClick, handleGridFolderClick]
  );

  // Render folder children recursively
  const renderFolderChildren = (parentId: string, siteId: string): JSX.Element | null => {
    const children = loadedChildren.get(parentId) || [];
    if (children.length === 0) {
      return null;
    }
    return (
      <Tree>
        {children.map((folder) => (
          <TreeItem
            key={folder.id}
            itemType="branch"
            value={folder.id}
            data-drop-target={dropTarget?.folderId === folder.id && dropTarget?.location === 'tree' ? 'true' : 'false'}
            className={dropTarget?.folderId === folder.id && dropTarget?.location === 'tree' ? styles.dropTargetHighlight : undefined}
            onDragOver={(e: React.DragEvent) => handleTreeFolderDragOver(e, folder.id, 'tree')}
            onDragLeave={handleTreeFolderDragLeave}
            onDrop={(e: React.DragEvent) => handleTreeFolderDrop(e, folder.id, siteId)}
          >
            <TreeItemLayout
              ref={selectedFolder?.folderId === folder.id ? selectedTreeItemRef : undefined}
              iconBefore={
                isChannelFolder(folder.name, siteId)
                  ? <TeamsIcon size={20} />
                  : <Folder20Filled className={styles.folderIcon} />
              }
              className={selectedFolder?.folderId === folder.id ? styles.selectedTreeItemLayout : undefined}
              onClick={(e) => {
                const target = e.target as HTMLElement;
                const isChevronClick = target.closest('[class*="expandIcon"]') !== null;
                if (!isChevronClick) {
                  e.stopPropagation();
                  handleItemClick(folder.id, folder.name, siteId);
                }
              }}
            >
              {folder.name}
            </TreeItemLayout>
            {openItems.has(folder.id) && renderFolderChildren(folder.id, siteId)}
          </TreeItem>
        ))}
      </Tree>
    );
  };

  // Handle column resize - only save to localStorage, don't update state
  // This prevents the hook from re-initializing and resetting widths
  const onColumnResize = useCallback(
    (_event: KeyboardEvent | TouchEvent | MouseEvent | undefined, data: { columnId: TableColumnId; width: number }) => {
      // Read current saved options from localStorage (or use defaults)
      const saved = localStorage.getItem(COLUMN_SIZING_KEY);
      let currentOptions: TableColumnSizingOptions;
      try {
        currentOptions = saved ? JSON.parse(saved) : columnSizingOptions;
      } catch {
        currentOptions = columnSizingOptions;
      }

      // Update with new width and save back to localStorage
      const newOptions = {
        ...currentOptions,
        [data.columnId]: {
          ...currentOptions[data.columnId],
          defaultWidth: data.width,
        },
      };
      localStorage.setItem(COLUMN_SIZING_KEY, JSON.stringify(newOptions));
    },
    [columnSizingOptions]
  );


  // Handle refresh button click
  const handleRefresh = useCallback(() => {
    if (selectedFolder) {
      // Refetch folder contents with refresh=true to bypass backend cache
      refetchFolderContents();
    } else {
      // Refetch sites list when no folder is selected
      refetchSites();
    }
  }, [selectedFolder, refetchFolderContents, refetchSites]);

  // Handle file input change (files selected for upload)
  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        uploadFiles(Array.from(files));
      }
      // Reset input value to allow re-selecting the same file
      event.target.value = '';
    },
    [uploadFiles]
  );

  // Handle folder input change (folder selected for upload)
  const handleFolderInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        // Use uploadFolder to preserve folder structure via webkitRelativePath
        uploadFolder(Array.from(files));
      }
      // Reset input value to allow re-selecting the same folder
      event.target.value = '';
    },
    [uploadFolder]
  );

  // Handle "Files" menu item click - trigger hidden file input
  const handleFilesMenuClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle "Folder" menu item click - trigger hidden folder input
  const handleFolderMenuClick = useCallback(() => {
    folderInputRef.current?.click();
  }, []);

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = useCallback(
    (item: BreadcrumbPathItem) => {
      // Update selected folder
      setSelectedFolder({
        siteId: item.siteId,
        folderId: item.folderId,
        name: item.name,
      });

      // Truncate breadcrumb path to this item
      const index = breadcrumbPath.findIndex((b) => b.id === item.id);
      if (index >= 0) {
        setBreadcrumbPath(breadcrumbPath.slice(0, index + 1));
      }
    },
    [breadcrumbPath]
  );

  // Handle clicking "Sites" root breadcrumb (clears selection)
  const handleSitesClick = useCallback(() => {
    setSelectedFolder(null);
    setBreadcrumbPath([]);
  }, []);

  // Handle search execution (on Enter key press)
  const handleSearchKeyDown = useCallback(
    async (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter' || !selectedFolder || !searchQuery.trim()) {
        return;
      }

      setIsSearching(true);
      try {
        const results = await TenantApiService.searchSite(
          tenantId,
          selectedFolder.siteId,
          searchQuery.trim()
        );
        setSearchResults(results);
      } catch {
        // Error handled silently - show empty results
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [tenantId, selectedFolder, searchQuery]
  );

  // Handle "Folder" menu item click - open new folder dialog
  const handleNewFolderClick = useCallback(() => {
    setNewFolderName('');
    setIsNewFolderDialogOpen(true);
  }, []);

  // Handle creating a new document and opening it in a new tab
  const handleCreateDocument = useCallback(
    async (documentType: 'word' | 'excel' | 'powerpoint' | 'visio') => {
      if (!selectedFolder) return;

      // Map document type to default filename (matches SharePoint naming convention)
      const filenameMap: Record<string, string> = {
        word: 'Document.docx',
        excel: 'Book.xlsx',
        powerpoint: 'Presentation.pptx',
        visio: 'Drawing.vsdx',
      };

      const filename = filenameMap[documentType];

      setIsCreatingDocument(true);
      try {
        // Use delegated API when token is available (user-attributed operations)
        const delegatedToken = await getDelegatedToken();

        const result = delegatedToken
          ? await TenantApiService.createDocumentDelegated(
              tenantId,
              selectedFolder.siteId,
              selectedFolder.folderId,
              filename,
              delegatedToken
            )
          : await TenantApiService.createDocument(
              tenantId,
              selectedFolder.siteId,
              selectedFolder.folderId,
              filename
            );

        // Open the document in edit mode in a new tab
        const editUrl = new URL(result.webUrl);
        editUrl.searchParams.set('action', 'edit');
        window.open(editUrl.toString(), '_blank');

        // Refresh folder contents to show the new document
        refetchFolderContents();
      } catch {
        // Error handled silently - could add error state here
      } finally {
        setIsCreatingDocument(false);
      }
    },
    [tenantId, selectedFolder, refetchFolderContents, getDelegatedToken]
  );

  // Handle creating a new folder
  const handleCreateFolder = useCallback(async () => {
    if (!selectedFolder || !newFolderName.trim()) return;

    const trimmedName = newFolderName.trim();

    // Store previous data for potential rollback
    const queryKey = folderContentsKeys.folder(tenantId, selectedFolder.siteId, selectedFolder.folderId);
    const previousData = queryClient.getQueryData<FolderItem[]>(queryKey);

    // Create optimistic folder item
    const tempId = `temp-folder-${Date.now()}`;
    const optimisticFolder: FolderItem = {
      id: tempId,
      name: trimmedName,
      isFolder: true,
      webUrl: '',
      size: 0,
      lastModifiedDateTime: new Date().toISOString(),
      lastModifiedBy: '',
      parentId: selectedFolder.folderId,
    };

    setIsCreatingFolder(true);

    // Optimistic update: Add folder to UI immediately
    if (previousData) {
      queryClient.setQueryData<FolderItem[]>(queryKey, [...previousData, optimisticFolder]);
    }

    // Close dialog immediately for better UX
    setIsNewFolderDialogOpen(false);
    setNewFolderName('');

    try {
      // Get delegated token for user attribution
      const delegatedToken = await getDelegatedToken();

      if (delegatedToken) {
        await TenantApiService.createFoldersDelegated(
          tenantId,
          selectedFolder.siteId,
          selectedFolder.folderId,
          [trimmedName],
          delegatedToken
        );
      } else {
        await TenantApiService.createFolders(
          tenantId,
          selectedFolder.siteId,
          selectedFolder.folderId,
          [trimmedName]
        );
      }

      setPasteToast({ type: 'success', message: `Created folder "${trimmedName}"` });

      // Refresh to get the real folder data from server
      refetchFolderContents();
    } catch {
      // Rollback optimistic update on error
      if (previousData) {
        queryClient.setQueryData<FolderItem[]>(queryKey, previousData);
      }

      setPasteToast({ type: 'error', message: `Failed to create folder "${trimmedName}"` });
    } finally {
      setIsCreatingFolder(false);
    }
  }, [tenantId, selectedFolder, newFolderName, queryClient, refetchFolderContents, getDelegatedToken]);

  // Handle delete button click
  const handleDeleteClick = useCallback(() => {
    if (selectedGridItems.size === 0) return;
    setIsDeleteDialogOpen(true);
  }, [selectedGridItems.size]);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedFolder || selectedGridItems.size === 0) return;

    const itemIds = Array.from(selectedGridItems);
    const itemCount = itemIds.length;

    // Store previous data for potential rollback
    const queryKey = folderContentsKeys.folder(tenantId, selectedFolder.siteId, selectedFolder.folderId);
    const previousData = queryClient.getQueryData<FolderItem[]>(queryKey);

    setIsDeleting(true);

    // Optimistic update: Remove items from UI immediately
    if (previousData) {
      queryClient.setQueryData<FolderItem[]>(
        queryKey,
        previousData.filter((item) => !itemIds.includes(item.id))
      );
    }

    // Close dialog and clear selection immediately for better UX
    setSelectedGridItems(new Set());
    setIsDeleteDialogOpen(false);

    try {
      // Use delegated API when token is available (user-attributed operations)
      const delegatedToken = await getDelegatedToken();

      // Delete all selected items in parallel
      await Promise.all(
        itemIds.map((itemId) =>
          delegatedToken
            ? TenantApiService.deleteItemDelegated(tenantId, selectedFolder.siteId, itemId, delegatedToken)
            : TenantApiService.deleteItem(tenantId, selectedFolder.siteId, itemId)
        )
      );

      setPasteToast({
        type: 'success',
        message: itemCount === 1
          ? 'Item moved to recycle bin'
          : `${itemCount} items moved to recycle bin`,
      });

      // Wait for Graph API to propagate the change before refetching
      await new Promise((resolve) => setTimeout(resolve, 1000));

      refetchFolderContents();
    } catch {
      // Rollback optimistic update on error
      if (previousData) {
        queryClient.setQueryData<FolderItem[]>(queryKey, previousData);
      }

      setPasteToast({
        type: 'error',
        message: itemCount === 1
          ? 'Failed to delete item'
          : `Failed to delete ${itemCount} items`,
      });
    } finally {
      setIsDeleting(false);
    }
  }, [tenantId, selectedFolder, selectedGridItems, queryClient, refetchFolderContents, getDelegatedToken]);

  // Handle download from action menu
  const handleDownload = useCallback(
    async (item: FolderItem) => {
      if (!selectedFolder) return;

      try {
        const { download_url } = await TenantApiService.getDownloadUrl(
          tenantId,
          selectedFolder.siteId,
          item.id
        );

        // Trigger download by creating a temporary link
        const link = document.createElement('a');
        link.href = download_url;
        link.download = item.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setPasteToast({ type: 'success', message: `Downloading "${item.name}"` });
      } catch {
        setPasteToast({ type: 'error', message: `Failed to download "${item.name}"` });
      }
    },
    [tenantId, selectedFolder]
  );

  // Get selected files (excluding folders) for download button
  const selectedFiles = useMemo(() => {
    if (selectedGridItems.size === 0) return [];
    return effectiveGridContents.filter(
      (item) => selectedGridItems.has(item.id) && !item.isFolder
    );
  }, [selectedGridItems, effectiveGridContents]);

  // Handle download from toolbar (downloads all selected files)
  const handleToolbarDownload = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    setIsDownloading(true);
    try {
      for (const file of selectedFiles) {
        await handleDownload(file);
      }
    } finally {
      setIsDownloading(false);
    }
  }, [selectedFiles, handleDownload]);

  // Handle "Go to folder" from search result context menu
  const handleGoToFolder = useCallback(
    (item: SearchResultItem) => {
      if (!selectedFolder) return;

      // Clear search state
      setSearchResults(null);
      setSearchQuery('');

      // Navigate to the parent folder of the search result
      const parentFolderId = item.parentReference.id;

      // If the parent is 'root', navigate to the site's root folder
      if (parentFolderId === 'root') {
        // The site root has the same ID as the site, but we need to refetch
        setSelectedFolder({
          siteId: selectedFolder.siteId,
          folderId: selectedFolder.siteId, // Site root
          name: selectedFolder.name,
        });
      } else {
        // Navigate to the specific folder
        // Backend enriches search results with parentReference.path
        setSelectedFolder({
          siteId: selectedFolder.siteId,
          folderId: parentFolderId,
          name: item.parentReference.path.split('/').pop() || 'Folder',
        });
      }
    },
    [selectedFolder]
  );

  // Open rename dialog for an item
  const openRenameDialog = useCallback((item: FolderItem) => {
    setItemToRename(item);
    setNewItemName(item.name);
    setIsRenameDialogOpen(true);
  }, []);

  // Handle rename confirmation
  const handleRenameConfirm = useCallback(async () => {
    if (!selectedFolder || !itemToRename || !newItemName.trim()) return;

    const oldName = itemToRename.name;
    const trimmedNewName = newItemName.trim();

    // Don't rename if name hasn't changed
    if (oldName === trimmedNewName) {
      setIsRenameDialogOpen(false);
      setItemToRename(null);
      setNewItemName('');
      return;
    }

    // Store previous data for potential rollback
    const queryKey = folderContentsKeys.folder(tenantId, selectedFolder.siteId, selectedFolder.folderId);
    const previousData = queryClient.getQueryData<FolderItem[]>(queryKey);

    setIsRenaming(true);

    // Optimistic update: Update item name in UI immediately
    if (previousData) {
      queryClient.setQueryData<FolderItem[]>(
        queryKey,
        previousData.map((item) =>
          item.id === itemToRename.id ? { ...item, name: trimmedNewName } : item
        )
      );
    }

    // Close dialog immediately for better UX
    setIsRenameDialogOpen(false);
    setItemToRename(null);
    setNewItemName('');

    try {
      // Use delegated API when token is available (user-attributed operations)
      const delegatedToken = await getDelegatedToken();
      if (delegatedToken) {
        await TenantApiService.renameItemDelegated(
          tenantId,
          selectedFolder.siteId,
          itemToRename.id,
          trimmedNewName,
          delegatedToken
        );
      } else {
        await TenantApiService.renameItem(
          tenantId,
          selectedFolder.siteId,
          itemToRename.id,
          trimmedNewName
        );
      }

      setPasteToast({ type: 'success', message: `Renamed to "${trimmedNewName}"` });

      // Wait for Graph API to propagate the change before refetching
      await new Promise((resolve) => setTimeout(resolve, 1000));

      refetchFolderContents();
    } catch {
      // Rollback optimistic update on error
      if (previousData) {
        queryClient.setQueryData<FolderItem[]>(queryKey, previousData);
      }

      setPasteToast({ type: 'error', message: `Failed to rename "${oldName}"` });
    } finally {
      setIsRenaming(false);
    }
  }, [tenantId, selectedFolder, itemToRename, newItemName, queryClient, refetchFolderContents, getDelegatedToken]);

  // Handle copy to clipboard
  const handleCopy = useCallback(
    (item: FolderItem) => {
      if (!selectedFolder) return;
      setClipboard({
        item,
        siteId: selectedFolder.siteId,
        sourceFolderId: selectedFolder.folderId,
        operation: 'copy',
      });
      setPasteToast({ type: 'success', message: `"${item.name}" copied to clipboard` });
    },
    [selectedFolder]
  );

  // Handle cut to clipboard
  const handleCut = useCallback(
    (item: FolderItem) => {
      if (!selectedFolder) return;
      setClipboard({
        item,
        siteId: selectedFolder.siteId,
        sourceFolderId: selectedFolder.folderId,
        operation: 'cut',
      });
      setPasteToast({ type: 'success', message: `"${item.name}" cut to clipboard` });
    },
    [selectedFolder]
  );

  // Handle delete single item from action menu
  const handleDeleteItem = useCallback(
    async (item: FolderItem) => {
      if (!selectedFolder) return;

      // Select the item and open delete dialog
      setSelectedGridItems(new Set([item.id]));
      setIsDeleteDialogOpen(true);
    },
    [selectedFolder]
  );

  // Handle paste from clipboard
  const handlePaste = useCallback(async () => {
    if (!clipboard || !selectedFolder) return;

    // Can only paste within the same site
    if (clipboard.siteId !== selectedFolder.siteId) {
      setPasteToast({ type: 'error', message: 'Cannot paste across different sites' });
      return;
    }

    // Cannot paste into same folder for cut
    if (clipboard.operation === 'cut' && clipboard.sourceFolderId === selectedFolder.folderId) {
      setPasteToast({ type: 'error', message: 'Item is already in this folder' });
      return;
    }

    const itemName = clipboard.item.name;
    const isCut = clipboard.operation === 'cut';

    // Store references for optimistic updates
    const sourceQueryKey = folderContentsKeys.folder(tenantId, clipboard.siteId, clipboard.sourceFolderId);
    const destQueryKey = folderContentsKeys.folder(tenantId, selectedFolder.siteId, selectedFolder.folderId);
    const previousSourceData = queryClient.getQueryData<FolderItem[]>(sourceQueryKey);
    const previousDestData = queryClient.getQueryData<FolderItem[]>(destQueryKey);

    setIsPasting(true);

    // Optimistic update: For cut, remove from source folder
    if (isCut && previousSourceData) {
      queryClient.setQueryData<FolderItem[]>(
        sourceQueryKey,
        previousSourceData.filter((item) => item.id !== clipboard.item.id)
      );
    }

    // Optimistic update: Add to destination folder (with temporary ID for copy)
    if (previousDestData) {
      const newItem: FolderItem = {
        ...clipboard.item,
        id: isCut ? clipboard.item.id : `temp-copy-${Date.now()}`,
        parentId: selectedFolder.folderId,
      };
      queryClient.setQueryData<FolderItem[]>(destQueryKey, [...previousDestData, newItem]);
    }

    try {
      // Use delegated API when token is available (user-attributed operations)
      const delegatedToken = await getDelegatedToken();

      if (isCut) {
        // Move item to current folder
        if (delegatedToken) {
          await TenantApiService.moveItemDelegated(
            tenantId,
            clipboard.siteId,
            clipboard.item.id,
            selectedFolder.folderId,
            delegatedToken
          );
        } else {
          await TenantApiService.moveItem(
            tenantId,
            clipboard.siteId,
            clipboard.item.id,
            selectedFolder.folderId
          );
        }
        // Clear clipboard after successful cut
        setClipboard(null);
        setPasteToast({ type: 'success', message: `Moved "${itemName}" successfully` });
      } else {
        // Copy item to current folder
        if (delegatedToken) {
          await TenantApiService.copyItemDelegated(
            tenantId,
            clipboard.siteId,
            clipboard.item.id,
            selectedFolder.folderId,
            delegatedToken
          );
        } else {
          await TenantApiService.copyItem(
            tenantId,
            clipboard.siteId,
            clipboard.item.id,
            selectedFolder.folderId
          );
        }
        setPasteToast({ type: 'success', message: `Copied "${itemName}" successfully` });
      }

      // Wait for Graph API to propagate the change before refetching
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Refresh both source and destination folders
      await Promise.all([
        refetchFolderContents(),
        // Refresh source folder cache if it's different from current
        clipboard.sourceFolderId !== selectedFolder.folderId
          ? TenantApiService.getFolderContents(tenantId, clipboard.siteId, clipboard.sourceFolderId, true)
              .then((freshData) => queryClient.setQueryData(sourceQueryKey, freshData))
              .catch(() => {}) // Ignore errors refreshing source
          : Promise.resolve(),
      ]);
    } catch (error) {
      // Rollback optimistic updates on error
      if (isCut && previousSourceData) {
        queryClient.setQueryData<FolderItem[]>(sourceQueryKey, previousSourceData);
      }
      if (previousDestData) {
        queryClient.setQueryData<FolderItem[]>(destQueryKey, previousDestData);
      }

      // Show user-friendly error message (dialog for locked files, toast for others)
      const operation = isCut ? 'move' : 'copy';
      const operationError = getFileOperationError(error, operation);
      if (operationError.isFileLocked) {
        setErrorDialog({
          title: 'File In Use',
          message: operationError.message,
        });
      } else {
        setPasteToast({
          type: 'error',
          message: operationError.message,
        });
      }
    } finally {
      setIsPasting(false);
    }
  }, [tenantId, selectedFolder, clipboard, queryClient, refetchFolderContents, getDelegatedToken]);

  // Grid columns for file/folder display
  // Location column is only included when showing search results
  const columns: TableColumnDefinition<FolderItem>[] = useMemo(() => {
    const baseColumns: TableColumnDefinition<FolderItem>[] = [
    createTableColumn<FolderItem>({
      columnId: 'name',
      compare: (a, b) => {
        // Sort folders before files, then alphabetically
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return a.name.localeCompare(b.name);
      },
      renderHeaderCell: () => 'Name',
      renderCell: (item) => (
        <div className={styles.fileItem}>
          {item.isFolder ? (
            isAtSitesRoot
              ? <SharePointIcon size={24} />
              : selectedFolder && isChannelFolder(item.name, selectedFolder.siteId)
                ? <TeamsIcon size={24} />
                : <Folder24Filled className={styles.folderIcon} />
          ) : (
            getFileIcon(item.name, styles)
          )}
          {item.isFolder ? (
            <span
              className={styles.folderLink}
              onClick={() => handleGridItemClick(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleGridItemClick(item);
                }
              }}
            >
              {item.name}
            </span>
          ) : (
            <span className={styles.fileName}>{item.name}</span>
          )}
          {/* Action menu button - appears on row hover */}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                icon={<MoreHorizontal20Regular />}
                size="small"
                className={mergeClasses(styles.actionMenuButton, 'action-menu-button')}
                aria-label={`Actions for ${item.name}`}
                onClick={(e) => e.stopPropagation()}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  icon={<Open20Regular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(item.webUrl, '_blank');
                  }}
                >
                  Open
                </MenuItem>
                {/* Go to folder - only for search results (items with parentReference) */}
                {'parentReference' in item && (item as unknown as SearchResultItem).parentReference && (
                  <MenuItem
                    icon={<FolderArrowRight20Regular />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGoToFolder(item as unknown as SearchResultItem);
                    }}
                  >
                    Go to folder
                  </MenuItem>
                )}
                {!item.isFolder && (
                  <MenuItem
                    icon={<ArrowDownload20Regular />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(item);
                    }}
                  >
                    Download
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Rename20Regular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openRenameDialog(item);
                  }}
                >
                  Rename
                </MenuItem>
                <MenuItem
                  icon={<Copy20Regular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy(item);
                  }}
                >
                  Copy
                </MenuItem>
                <MenuItem
                  icon={<Cut20Regular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCut(item);
                  }}
                >
                  Cut
                </MenuItem>
                <MenuItem
                  icon={<Delete24Regular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem(item);
                  }}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      ),
    }),
    createTableColumn<FolderItem>({
      columnId: 'modified',
      compare: (a, b) =>
        new Date(a.lastModifiedDateTime).getTime() - new Date(b.lastModifiedDateTime).getTime(),
      renderHeaderCell: () => 'Date Modified',
      renderCell: (item) => (
        <span className={styles.dateColumn}>
          {item.parentId === 'sites-root' ? '' : formatDate(item.lastModifiedDateTime)}
        </span>
      ),
    }),
    createTableColumn<FolderItem>({
      columnId: 'modifiedBy',
      compare: (a, b) => (a.lastModifiedBy ?? '').localeCompare(b.lastModifiedBy ?? ''),
      renderHeaderCell: () => 'Modified By',
      renderCell: (item) => (
        <span className={styles.dateColumn}>
          {item.parentId === 'sites-root' ? '' : (item.lastModifiedBy ?? '-')}
        </span>
      ),
    }),
    createTableColumn<FolderItem>({
      columnId: 'size',
      compare: (a, b) => (a.size ?? 0) - (b.size ?? 0),
      renderHeaderCell: () => 'Size',
      renderCell: (item) => (
        <span className={styles.sizeColumn}>
          {item.parentId === 'sites-root' ? '' : (item.isFolder ? '-' : formatFileSize(item.size ?? 0))}
        </span>
      ),
    }),
    ];

    // Add Location column only when showing search results
    if (searchResults !== null) {
      // Insert Location column after Name column (index 1)
      // Backend enriches search results with parentReference.path
      baseColumns.splice(1, 0, createTableColumn<FolderItem>({
        columnId: 'location',
        compare: (a, b) => {
          const pathA = 'parentReference' in a ? (a as unknown as SearchResultItem).parentReference.path : '';
          const pathB = 'parentReference' in b ? (b as unknown as SearchResultItem).parentReference.path : '';
          return pathA.localeCompare(pathB);
        },
        renderHeaderCell: () => 'Location',
        renderCell: (item) => (
          <span className={styles.dateColumn}>
            {'parentReference' in item ? (item as unknown as SearchResultItem).parentReference.path : ''}
          </span>
        ),
      }));
    }

    return baseColumns;
  }, [searchResults, isAtSitesRoot, selectedFolder, handleGridItemClick, handleDownload, openRenameDialog, handleCopy, handleCut, handleDeleteItem, handleGoToFolder, styles, isChannelFolder]);

  // Determine loading state for grid
  const isGridLoading = isLoadingContents || isLoadingGrid;
  const isGridRefreshing = isFetchingGrid && !isLoadingGrid;

  return (
    <div className={styles.container} ref={containerRef} data-testid="tree-file-browser-container" style={{ height: `${containerHeight}px` }}>
      {/* Hidden file inputs for upload functionality */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
        multiple
        aria-hidden="true"
      />
      <input
        type="file"
        ref={folderInputRef}
        style={{ display: 'none' }}
        onChange={handleFolderInputChange}
        // @ts-expect-error - webkitdirectory is a non-standard attribute
        webkitdirectory=""
        multiple
        aria-hidden="true"
      />

      {/* Toolbar - spans full width above both panels */}
      <Toolbar className={styles.toolbar} aria-label="File browser actions">
        <AsyncToolbarButton
          label="Refresh"
          loadingLabel="Refreshing..."
          icon={<ArrowClockwise24Regular />}
          onClick={handleRefresh}
          loading={selectedFolder ? isFetchingGrid : sitesFetching}
        />
        {/* Spacer to push remaining buttons to the right */}
        <div style={{ flex: 1 }} />
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <AsyncToolbarButton
              label="New"
              loadingLabel="Creating..."
              icon={<Add24Regular />}
              disabled={!selectedFolder}
              loading={isCreatingDocument}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<Folder24Filled className={styles.folderIcon} />} onClick={handleNewFolderClick}>New folder</MenuItem>
              <MenuItem icon={<img src={fileIcons['docx']} alt="" width={20} height={20} />} onClick={() => handleCreateDocument('word')}>Word document</MenuItem>
              <MenuItem icon={<img src={fileIcons['xlsx']} alt="" width={20} height={20} />} onClick={() => handleCreateDocument('excel')}>Excel workbook</MenuItem>
              <MenuItem icon={<img src={fileIcons['pptx']} alt="" width={20} height={20} />} onClick={() => handleCreateDocument('powerpoint')}>PowerPoint presentation</MenuItem>
              <MenuItem icon={<img src={fileIcons['vsdx']} alt="" width={20} height={20} />} onClick={() => handleCreateDocument('visio')}>Visio drawing</MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <AsyncToolbarButton
              label="Upload"
              loadingLabel="Uploading..."
              icon={<ArrowUpload24Regular />}
              disabled={!selectedFolder}
              loading={isUploading}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<Document24Regular />} onClick={handleFilesMenuClick}>Files</MenuItem>
              <MenuItem icon={<FolderOpen24Regular />} onClick={handleFolderMenuClick}>Folder</MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
        <AsyncToolbarButton
          label="Paste"
          loadingLabel="Pasting..."
          icon={<ClipboardPaste24Regular />}
          onClick={handlePaste}
          disabled={!clipboard || !selectedFolder}
          loading={isPasting}
        />
        <AsyncToolbarButton
          label="Download"
          loadingLabel="Downloading..."
          icon={<ArrowDownload24Regular />}
          onClick={handleToolbarDownload}
          disabled={selectedFiles.length === 0}
          loading={isDownloading}
        />
        <AsyncToolbarButton
          label="Delete"
          loadingLabel="Deleting..."
          icon={<Delete24Regular />}
          onClick={handleDeleteClick}
          disabled={selectedGridItems.size === 0}
          loading={isDeleting}
        />
      </Toolbar>

      {/* Delete confirmation dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(_, data) => !isDeleting && setIsDeleteDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete items?</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete {selectedGridItems.size} item{selectedGridItems.size !== 1 ? 's' : ''}?
                Items will be moved to the site&apos;s recycle bin.
              </Text>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isDeleting}>Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Rename dialog */}
      <Dialog
        open={isRenameDialogOpen}
        onOpenChange={(_, data) => !isRenaming && setIsRenameDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Rename</DialogTitle>
            <DialogContent>
              <Label htmlFor="rename-input">New name</Label>
              <Input
                id="rename-input"
                aria-label="New name"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="Enter new name"
                style={{ width: '100%', marginTop: '8px' }}
              />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isRenaming}>Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleRenameConfirm}
                disabled={!newItemName.trim() || isRenaming}
              >
                {isRenaming ? 'Renaming...' : 'Rename'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Split panels container */}
      <div className={styles.splitPanels}>
        {/* Left panel - Tree */}
        <div
          className={styles.treePanel}
          data-testid="tree-panel"
          style={{ width: `${navPaneWidth}px` }}
        >
        {sitesLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="small" label="Loading sites..." />
          </div>
        ) : sites.length === 0 ? (
          <div className={styles.emptyContainer}>
            <Text>No sites available</Text>
          </div>
        ) : (
          <Tree
            aria-label="File browser tree"
            openItems={openItems}
            onOpenChange={handleOpenChange}
          >
            {sites.map((site) => (
              <TreeItem
                key={site.id}
                itemType="branch"
                value={site.id}
                data-drop-target={dropTarget?.folderId === site.id && dropTarget?.location === 'tree' ? 'true' : 'false'}
                className={dropTarget?.folderId === site.id && dropTarget?.location === 'tree' ? styles.dropTargetHighlight : undefined}
                onDragOver={(e: React.DragEvent) => handleTreeFolderDragOver(e, site.id, 'tree')}
                onDragLeave={handleTreeFolderDragLeave}
                onDrop={(e: React.DragEvent) => handleTreeFolderDrop(e, site.id, site.id)}
              >
                <TreeItemLayout
                  iconBefore={<SharePointIcon size={20} />}
                  className={mergeClasses(
                    selectedFolder?.folderId === site.id ? styles.selectedTreeItemLayout : undefined,
                    dropTarget?.folderId === site.id && dropTarget?.location === 'tree' ? styles.dropTargetHighlight : undefined
                  )}
                  onClick={(e) => {
                    // Only handle selection if NOT clicking on expand/collapse chevron
                    const target = e.target as HTMLElement;
                    const isChevronClick = target.closest('[class*="expandIcon"]') !== null;
                    if (!isChevronClick) {
                      e.stopPropagation();
                      handleItemClick(site.id, site.displayName, site.id);
                    }
                  }}
                >
                  <span
                    style={{ display: 'inline-block', width: '100%' }}
                    onDragOver={(e: React.DragEvent) => handleTreeFolderDragOver(e, site.id, 'tree')}
                    onDragLeave={handleTreeFolderDragLeave}
                    onDrop={(e: React.DragEvent) => handleTreeFolderDrop(e, site.id, site.id)}
                  >
                    {site.displayName}
                  </span>
                </TreeItemLayout>
                {openItems.has(site.id) && (
                  <Tree>
                    {(loadedChildren.get(site.id) || []).map((folder) => (
                      <TreeItem
                        key={folder.id}
                        itemType="branch"
                        value={folder.id}
                        data-drop-target={dropTarget?.folderId === folder.id && dropTarget?.location === 'tree' ? 'true' : 'false'}
                        className={dropTarget?.folderId === folder.id && dropTarget?.location === 'tree' ? styles.dropTargetHighlight : undefined}
                        onDragOver={(e: React.DragEvent) => handleTreeFolderDragOver(e, folder.id, 'tree')}
                        onDragLeave={handleTreeFolderDragLeave}
                        onDrop={(e: React.DragEvent) => handleTreeFolderDrop(e, folder.id, site.id)}
                      >
                        <TreeItemLayout
                          ref={selectedFolder?.folderId === folder.id ? selectedTreeItemRef : undefined}
                          iconBefore={
                            isChannelFolder(folder.name, site.id)
                              ? <TeamsIcon size={20} />
                              : <Folder20Filled className={styles.folderIcon} />
                          }
                          className={selectedFolder?.folderId === folder.id ? styles.selectedTreeItemLayout : undefined}
                          onClick={(e) => {
                            const target = e.target as HTMLElement;
                            const isChevronClick = target.closest('[class*="expandIcon"]') !== null;
                            if (!isChevronClick) {
                              e.stopPropagation();
                              handleItemClick(folder.id, folder.name, site.id);
                            }
                          }}
                        >
                          {folder.name}
                        </TreeItemLayout>
                        {openItems.has(folder.id) && renderFolderChildren(folder.id, site.id)}
                      </TreeItem>
                    ))}
                  </Tree>
                )}
              </TreeItem>
            ))}
          </Tree>
        )}
      </div>

      {/* Resize handle - uses slider role for accessibility */}
      <div
        role="slider"
        aria-label="Resize navigation pane"
        aria-valuenow={navPaneWidth}
        aria-valuemin={MIN_NAV_PANE_WIDTH}
        aria-valuemax={500}
        aria-orientation="horizontal"
        tabIndex={0}
        className={mergeClasses(
          styles.resizeHandle,
          isResizing && styles.resizeHandleActive
        )}
        onMouseDown={handleResizeMouseDown}
        onKeyDown={(event) => {
          // Allow keyboard resize with arrow keys
          if (event.key === 'ArrowLeft') {
            setNavPaneWidth((prev) => Math.max(MIN_NAV_PANE_WIDTH, prev - 10));
          } else if (event.key === 'ArrowRight') {
            setNavPaneWidth((prev) => {
              const maxWidth = containerRef.current
                ? containerRef.current.getBoundingClientRect().width * MAX_NAV_PANE_WIDTH_PERCENT
                : 500;
              return Math.min(maxWidth, prev + 10);
            });
          }
        }}
        style={{ cursor: 'col-resize' }}
      />

        {/* Right panel - Content (breadcrumb, grid) */}
        <div className={styles.contentPanel}>
          {/* Breadcrumb navigation and search */}
          <div className={styles.breadcrumbContainer}>
            <nav aria-label="Breadcrumb">
              {searchResults !== null ? (
                <div className={styles.searchResultsIndicator} data-testid="search-results-indicator">
                  <Search20Regular />
                  <Text weight="semibold">Search results for: {searchQuery}</Text>
                </div>
              ) : (
                <Breadcrumb>
                  <BreadcrumbItem>
                    {breadcrumbPath.length > 0 ? (
                      <BreadcrumbButton icon={<Home20Regular />} onClick={handleSitesClick}>
                        Sites
                      </BreadcrumbButton>
                    ) : (
                      <BreadcrumbButton icon={<Home20Regular />} current>
                        Sites
                      </BreadcrumbButton>
                    )}
                  </BreadcrumbItem>
                  {breadcrumbPath.map((item, index) => (
                    <BreadcrumbItem key={item.id}>
                      <BreadcrumbDivider />
                      {index === breadcrumbPath.length - 1 ? (
                        <BreadcrumbButton current>{item.name}</BreadcrumbButton>
                      ) : (
                        <BreadcrumbButton onClick={() => handleBreadcrumbClick(item)}>
                          {item.name}
                        </BreadcrumbButton>
                      )}
                    </BreadcrumbItem>
                  ))}
                </Breadcrumb>
              )}
            </nav>
            <Input
              type="search"
              placeholder={selectedFolder ? `Search ${sites.find(s => s.id === selectedFolder.siteId)?.displayName ?? 'site'}` : 'Search'}
              aria-label="Search files"
              className={styles.searchInput}
              contentBefore={<Search20Regular />}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                // Clear search results when input is cleared (e.g., via native X button)
                if (e.target.value === '' && searchResults !== null) {
                  setSearchResults(null);
                }
              }}
              onKeyDown={handleSearchKeyDown}
              disabled={!selectedFolder}
            />
          </div>

          {/* Grid panel */}
          <div className={styles.gridPanel}>
            {/* Grid content - click to clear selection */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              className={mergeClasses(
                styles.gridContent,
                isDragOver && styles.gridContentDropActive
              )}
              onClick={handleGridEmptySpaceClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="grid-drop-zone"
              data-drop-active={isDragOver ? 'true' : 'false'}
            >
              {/* Loading overlay - shown on top of DataGrid to preserve column sizing state */}
              {(isGridLoading || isSearching) && (
                <div className={styles.loadingOverlay}>
                  <Spinner size="large" label={isSearching ? 'Searching...' : `Loading ${selectedFolder?.name ?? 'folder'}...`} />
                </div>
              )}
              {/* Empty states - shown when no folder selected AND no sites available */}
              {!isGridLoading && !isSearching && !selectedFolder && sites.length === 0 && (
                <div className={styles.emptyContainer}>
                  <Folder24Filled className={mergeClasses(styles.emptyIcon, styles.folderIcon)} />
                  <Text weight="semibold">Select a site or folder</Text>
                  <Text size={200}>Click on a site in the tree to view its contents</Text>
                </div>
              )}
              {/* Empty search results */}
              {!isGridLoading && !isSearching && searchResults !== null && searchResults.length === 0 && (
                <div className={styles.emptyContainer} data-testid="search-empty-state">
                  <Search20Regular className={styles.emptyIcon} />
                  <Text weight="semibold">No results found</Text>
                  <Text size={200}>No files or folders match &quot;{searchQuery}&quot;</Text>
                </div>
              )}
              {/* Empty folder (not during search) */}
              {!isGridLoading && !isSearching && selectedFolder && searchResults === null && gridContents.length === 0 && (
                <div className={styles.emptyContainer}>
                  <Folder24Filled className={mergeClasses(styles.emptyIcon, styles.folderIcon)} />
                  <Text weight="semibold">This folder is empty</Text>
                </div>
              )}
              {/* DataGrid - always mounted to preserve column sizing state */}
              <DataGrid
                items={searchResults !== null ? (searchResults as unknown as FolderItem[]) : effectiveGridContents}
                columns={columns}
                getRowId={(item) => item.id}
                sortable
                resizableColumns
                columnSizingOptions={columnSizingOptions}
                onColumnResize={onColumnResize}
                className={styles.dataGrid}
                style={{ display: (!isGridLoading && !isSearching && ((selectedFolder && ((searchResults !== null && searchResults.length > 0) || (searchResults === null && gridContents.length > 0))) || (isAtSitesRoot && sites.length > 0))) ? undefined : 'none' }}
              >
                  <DataGridHeader className={styles.gridHeader}>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell className={styles.gridHeaderCell}>
                          {renderHeaderCell()}
                          {isGridRefreshing && <Spinner size="tiny" style={{ marginLeft: 8 }} />}
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody<FolderItem>>
                    {({ item, rowId }) => (
                      <DataGridRow<FolderItem>
                        key={rowId}
                        aria-selected={selectedGridItems.has(item.id)}
                        onClick={(e: React.MouseEvent) => handleGridRowClick(e, item)}
                        draggable
                        onDragStart={(e: React.DragEvent) => handleRowDragStart(e, item)}
                        onDragEnd={handleRowDragEnd}
                        data-drop-target={item.isFolder && dropTarget?.folderId === item.id && dropTarget?.location === 'grid' ? 'true' : 'false'}
                        onDragOver={item.isFolder && selectedFolder ? (e: React.DragEvent) => handleTreeFolderDragOver(e, item.id, 'grid') : handleRowDragOver}
                        onDragLeave={item.isFolder && selectedFolder ? handleTreeFolderDragLeave : undefined}
                        onDrop={item.isFolder && selectedFolder ? (e: React.DragEvent) => handleTreeFolderDrop(e, item.id, selectedFolder.siteId) : undefined}
                        style={{ cursor: 'pointer' }}
                        className={mergeClasses(
                          styles.gridRow,
                          selectedGridItems.has(item.id) && styles.selectedRow,
                          (item.isFolder && selectedFolder && dropTarget?.folderId === item.id && dropTarget?.location === 'grid') ? styles.dropTargetHighlight : undefined
                        )}
                      >
                        {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
            </div>
          </div>
        </div>
      </div>

      {/* Height resize handle - horizontal bar at bottom */}
      <div
        role="slider"
        aria-label="Resize container height"
        aria-valuenow={containerHeight}
        aria-valuemin={MIN_CONTAINER_HEIGHT}
        aria-valuemax={MAX_CONTAINER_HEIGHT}
        aria-orientation="vertical"
        tabIndex={0}
        className={mergeClasses(
          styles.heightResizeHandle,
          isResizingHeight && styles.heightResizeHandleActive
        )}
        onMouseDown={handleHeightResizeMouseDown}
        onKeyDown={(event) => {
          // Allow keyboard resize with arrow keys
          if (event.key === 'ArrowUp') {
            setContainerHeight((prev) => Math.max(MIN_CONTAINER_HEIGHT, prev - 20));
          } else if (event.key === 'ArrowDown') {
            setContainerHeight((prev) => Math.min(MAX_CONTAINER_HEIGHT, prev + 20));
          }
        }}
        style={{ cursor: 'row-resize' }}
      />

      {/* Upload progress toast */}
      {isUploading && (
        <div className={styles.uploadToast} role="status" aria-live="polite">
          <Spinner size="tiny" />
          <Text>Uploading files...</Text>
        </div>
      )}

      {/* Upload error toast */}
      {uploadError && !isUploading && (
        <div className={styles.uploadToast} role="alert" aria-live="assertive" style={{ borderColor: 'red' }}>
          <Text style={{ color: 'red' }}>Upload failed: {uploadError}</Text>
        </div>
      )}

      {/* Paste operation toast */}
      {pasteToast && (
        <div
          className={styles.uploadToast}
          role={pasteToast.type === 'error' ? 'alert' : 'status'}
          aria-live={pasteToast.type === 'error' ? 'assertive' : 'polite'}
          style={{
            borderColor: pasteToast.type === 'error' ? 'red' : tokens.colorBrandForeground1,
          }}
        >
          <Text style={{ color: pasteToast.type === 'error' ? 'red' : undefined }}>
            {pasteToast.message}
          </Text>
        </div>
      )}

      {/* New Folder Dialog */}
      <Dialog
        open={isNewFolderDialogOpen}
        onOpenChange={(_, data) => setIsNewFolderDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>New Folder</DialogTitle>
            <DialogContent>
              <Label htmlFor="folder-name-input">Folder name</Label>
              <Input
                id="folder-name-input"
                aria-label="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name"
                style={{ width: '100%', marginTop: '8px' }}
              />
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || isCreatingFolder}
              >
                {isCreatingFolder ? 'Creating...' : 'Create'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Error Dialog (for prominent errors like file-in-use) */}
      <Dialog
        open={errorDialog !== null}
        onOpenChange={(_, data) => !data.open && setErrorDialog(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{errorDialog?.title}</DialogTitle>
            <DialogContent>
              {errorDialog?.message}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="primary">OK</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
